import { HttpBackend, HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

export interface TranslationResource {
  prefix: string;
  suffix?: string;
  optional?: boolean;
}

export class MultiTranslateHttpFallbackLoader implements TranslateLoader {
  private http: HttpClient;

  constructor(_handler: HttpBackend, private _resourcesPrefix: (string | TranslationResource)[]) {
    this.http = new HttpClient(_handler);
  }

  public getTranslation(lang: string): Observable<any> {
    const requests = this._resourcesPrefix.map((resource) => this.loadWithFallbacks(lang, resource));

    return forkJoin(requests).pipe(map((response) => response.reduce((acc, curr) => this.mergeDeep(acc, curr), {})));
  }

  private loadWithFallbacks(lang: string, resource: string | TranslationResource): Observable<any> {
    const fallbackLangs = [lang];

    if (lang.length > 2) {
      fallbackLangs.push(lang.substring(0, 2));
    }
    if (!fallbackLangs.includes('en')) {
      fallbackLangs.push('en');
    }

    const tryLoad = (index: number): Observable<any> => {
      if (index >= fallbackLangs.length) return of({});

      return this.getTranslationRequest(fallbackLangs[index], resource).pipe(
        catchError((err) => {
          this.logError(fallbackLangs.slice(0, index + 1), err, resource);
          return tryLoad(index + 1);
        })
      );
    };

    return tryLoad(0);
  }

  private getTranslationRequest(lang: string, resource: string | TranslationResource) {
    let path: string;

    if (typeof resource === 'string') path = `${resource}${lang}.json`;
    else path = `${resource.prefix}${lang}${resource.suffix ?? '.json'}`;

    return this.http.get(path);
  }

  private mergeDeep(target: any, source: any): any {
    const output = Object.assign({}, target);
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach((key: any) => {
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, { [key]: source[key] });
          } else {
            output[key] = this.mergeDeep(target[key], source[key]);
          }
        } else {
          Object.assign(output, { [key]: source[key] });
        }
      });
    }
    return output;
  }

  private isObject(item: any): boolean {
    return item && typeof item === 'object' && !Array.isArray(item);
  }

  private logError(triedLangs: string[], error: any, resource: string | TranslationResource) {
    const optional = typeof resource !== 'string' && resource.optional;
    if (typeof error !== 'string' && !optional) {
      console.group();
      console.error(`Failed to load translations for languages: ${triedLangs.join(', ')}`);
      console.error(error);
      console.groupEnd();
    }
  }
}
